import { graphql } from 'gatsby';

import { Language, NavigationLinks } from '@mewa/types';

import { AccordionData } from './content-components/accordion';
import { ArticleReferenceData } from './content-components/article-reference';
import { ButtonData } from './content-components/button';
import { ClaimTextData } from './content-components/claim-text';
import { ClaimWithContentData } from './content-components/claim-with-content/claim-with-content';
import { CollectionSliderData } from './content-components/collections-slider/collection-slider';
import { ConsultationTeaserData } from './content-components/consultation-teaser';
import { ContentSliderData } from './content-components/content-slider';
import { ConversionTeaserData } from './content-components/conversion-teaser-variants';
import { DownloadData } from './content-components/download';
import { FlexibleContentData } from './content-components/flexible-content';
import { Heading2Data } from './content-components/heading-2';
import { Heading2CenterData } from './content-components/heading-2-center';
import { HeroImageData } from './content-components/hero-image';
import { IconGridData } from './content-components/icon-grid';
import { ImageSlideWithNumbersData } from './content-components/image-slide';
import { ImageSlideshowData } from './content-components/image-slideshow';
import { ImageWithTextOverlayData } from './content-components/image-with-text-overlay';
import { MediaGalleryData } from './content-components/media-gallery';
import { ParallaxImageData } from './content-components/parallax-image';
import { PersonTileData } from './content-components/person-tile';
import { QuoteSlideshowData } from './content-components/quote-slideshow';
import { SapFormData } from './content-components/sap-form';
import { SharedMewaCircleData } from './content-components/shared-mewa-circle';
import { ShortTextTeaserData } from './content-components/short-text-teaser';
import { SingleQuoteData } from './content-components/single-quote';
import { SpacerData } from './content-components/spacer';
import { StickyNavigationSlideshowData } from './content-components/sticky-navigation-slideshow';
import { TeaserWithImageData } from './content-components/teaser-with-image';
import { VideoWithCaptionData } from './content-components/video-with-caption';
import { ResponsiveVideoData } from './content-components/video/types';
import { StrapiComponent } from './live-preview/types';

export const ContentSectionFragment = graphql`
  fragment ContentSectionFragment on DYNAMIC_CONTENT_PAGE {
    content {
      ...ArticleReferenceFragment
      ...ButtonFragment
      ...ClaimTextFragment
      ...ClaimWithContentFragment
      ...CollectionSliderFragment
      ...ContentSliderFragment
      ...ConsultationTeaserFragment
      ...ConversionTeaserFragment
      ...DownloadFragment
      ...FlexibleContentFragment
      ...Heading2Fragment
      ...Heading2CenterFragment
      ...HeroImageFragment
      ...IconGridFragment
      ...ImageSlideshowFragment
      ...ImageSlideWithNumbersFragment
      ...ImageWithTextOverlayFragment
      ...MarkdownAccordionFragment
      ...MediaGalleryFragment
      ...ParallaxImageFragment
      ...PersonTileFragment
      ...QuoteSlideshowFragment
      ...ResponsiveVideoFragment
      ...SapFormFragment
      ...SharedMewaCircleFragment
      ...SingleQuoteFragment
      ...SpacerFragment
      ...StickyNavigationSlideshowFragment
      ...TeaserWithImageFragment
      ...VideoWithCaptionFragment
      ...ShortTextTeaserFragment
    }
  }
`;

export type PossibleContentSections =
  | AccordionData
  | ClaimTextData
  | ClaimWithContentData
  | ContentSliderData
  | ConsultationTeaserData
  | ConversionTeaserData
  | Heading2Data
  | Heading2CenterData
  | HeroImageData
  | IconGridData
  | ImageSlideshowData
  | ImageSlideWithNumbersData
  | ImageWithTextOverlayData
  | ParallaxImageData
  | QuoteSlideshowData
  | ResponsiveVideoData
  | SharedMewaCircleData
  | StickyNavigationSlideshowData
  | TeaserWithImageData
  | CollectionSliderData
  | ButtonData
  | SapFormData
  | StrapiComponent
  | FlexibleContentData
  | PersonTileData
  | SpacerData
  | SingleQuoteData
  | DownloadData
  | VideoWithCaptionData
  | MediaGalleryData
  | ShortTextTeaserData
  | ArticleReferenceData;

export type Section = {
  id?: string;
  heading?: string;
};

export type ContentSectionsTranslations = {
  author_box_by: string;
};

export const ContentSectionTranslationsFragment = graphql`
  fragment ContentSectionTranslations on Translation {
    author_box_by
  }
`;

export type ContentSectionItem<Props = PossibleContentSections> = Section &
  Props;

export type ContentSectionContextProps = {
  lang: Language;
  locale: string;
  translations: ContentSectionsTranslations;
  navigationLinks: NavigationLinks;
  langPrefix: string;
  conversion: {
    link: string;
    label: string;
    onClick?: () => void;
  };
  piwikEventNameForHeaderCTAButton?: string;
  sapForm?: SapFormData | undefined;
  componentIndex: number;
};

export type ComponentProps<Props> = ContentSectionItem<Props> &
  ContentSectionContextProps;

export type ConfigType<Props extends PossibleContentSections> = {
  componentName: string;
  isComponent: (section: PossibleContentSections) => section is Section & Props;
  Component: React.ComponentType<ComponentProps<Props>>;
  addTableOfContentsHeadline?: (data: Props) => ContentSectionItem<Props>;
};

export type GenericConfig = <R>(
  extract: <Props extends PossibleContentSections>(
    config: ConfigType<Props>,
  ) => R,
) => R;

export const getGenericSectionConfig =
  <Props extends PossibleContentSections>(
    i: ConfigType<Props>,
  ): GenericConfig =>
  (extract) =>
    extract(i);
